import * as R from 'ramda'

import InputNumber from 'components/uiKit/InputNumber'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import type { IImageEditValue } from '../ImageEditModal'
import * as s from './SizeFields.module.scss'

interface ISizeFieldsProps {
  localValue: IImageEditValue
  setLocalValue: (val: React.SetStateAction<IImageEditValue>) => void
  ih?: number
  iw?: number
  imgElement: HTMLImageElement | null
}

const SizeFields = ({ localValue, setLocalValue, ih, iw, imgElement }: ISizeFieldsProps) => {
  const updateCropParams = (dimension: 'width' | 'height', newValue?: number | null) => {
    const value = Number(newValue) || 0

    setLocalValue((val: IImageEditValue) => {
      const { cropParams } = val
      const { aspect, crop } = cropParams || {}
      const { width = iw, height = ih } = crop || {}

      const calculateDimensions = () => {
        if (aspect && imgElement) {
          return dimension === 'width'
            ? { width: value, height: Math.round(value / aspect) }
            : { width: Math.round(value * aspect), height: value }
        }

        return {
          width: (dimension === 'width' ? value : width) as number,
          height: (dimension === 'height' ? value : height) as number,
        }
      }
      const newDimensions = calculateDimensions()
      return {
        ...val,
        cropParams: {
          ...cropParams,
          crop: {
            ...crop,
            ...newDimensions,
            unit: 'px',
            x: crop?.x || 0,
            y: crop?.y || 0,
          },
        },
      }
    })
  }

  const handleWidthChange = (newWidth?: number | null) => updateCropParams('width', newWidth)

  const handleHeightChange = (newHeight?: number | null) => updateCropParams('height', newHeight)

  const width = localValue.cropParams?.crop?.width
  const height = localValue.cropParams?.crop?.height
  const x = localValue.cropParams?.crop?.x || 0
  const y = localValue.cropParams?.crop?.y || 0
  const min = 0
  const maxWidth = (iw || 0) - x
  const maxHeight = (ih || 0) - y

  const handleBlur = () => {
    updateCropParams('width', R.clamp(min, maxWidth, width || 0))
    updateCropParams('height', R.clamp(min, maxHeight, height || 0))
  }

  return (
    <>
      <div className={s.group}>{t('common.settings')}</div>
      <div className={s.fieldLayout}>
        <div className={s.label}>{t('input.label.size')}</div>
        <div className={s.field}>
          <InputNumber
            error={(width || 0) < 0 || (width || 0) > maxWidth}
            max={iw}
            min={0}
            name='width'
            onBlur={handleBlur}
            onChange={handleWidthChange}
            prefix={<span className={s.prefix}>{t('modal.editorCrop.placeholderW')}:&nbsp; </span>}
            size={KitSize.S}
            value={width ?? iw}
            width={78}
          />
          <InputNumber
            error={(height || 0) < 0 || (height || 0) > maxHeight}
            max={ih}
            min={0}
            name='height'
            onBlur={handleBlur}
            onChange={handleHeightChange}
            prefix={<span className={s.prefix}>{t('modal.editorCrop.placeholderH')}:&nbsp; </span>}
            size={KitSize.S}
            value={height ?? ih}
            width={78}
          />
        </div>
      </div>
    </>
  )
}

export default SizeFields
